import {inject, Injectable} from '@angular/core';
import {forkJoin, map, Observable, switchMap, throwError} from 'rxjs';
import {EntityService} from '../api-services/entity.service';
import {AuthService} from '../api-services/auth.service';
import {Entity} from '../models/entities/entity/entity.model';

export const ARRIVAL_STATUS_NAME = 'Arrival to final POD';
export const ARRIVAL_REMINDER_PARAM = 'arrival_status';
export const DEPARTURE_REMINDER_PARAM = 'departure_status';
export const DEPARTURE_STATUS_NAME = 'Departure from POL';
export interface PreparedArrivalStatus {truckId: number; mode: 'create' | 'edit' | 'view'; form: Entity}

export function arrivalReminderPath(path: string, enabled = true, departure = false): string {
  return enabled && /^\/dashboard\/form\/transporting_units\/[1-9]\d*\?top=check_points$/.test(path)
    ? path + '&' + (departure ? DEPARTURE_REMINDER_PARAM : ARRIVAL_REMINDER_PARAM) + '=actual' : path;
}

/** Prepare native forms through existing authenticated reads. Never stores or updates. */
@Injectable({providedIn: 'root'})
export class ArrivalStatusReminderService {
  private readonly entities = inject(EntityService);
  private readonly auth = inject(AuthService);

  prepare(truckId: number, statusName: typeof ARRIVAL_STATUS_NAME | typeof DEPARTURE_STATUS_NAME = ARRIVAL_STATUS_NAME): Observable<PreparedArrivalStatus> {
    if (![ARRIVAL_STATUS_NAME, DEPARTURE_STATUS_NAME].includes(statusName) || !Number.isSafeInteger(truckId) || truckId <= 0
      || !this.auth.can('transporting_units', 'show') || !this.auth.can('check_points', 'list')
      || !this.auth.can('check_points', 'show')) {
      return throwError(() => new Error('You do not have access to this Status check.'));
    }
    return this.entities.search('check_points', 'date_types', 'type', statusName,
      'dropdown', 1, null, null).pipe(switchMap(response => {
      const options = response?.data;
      if (!Array.isArray(options)) throw new Error('Could not find the Status check type.');
      const exact = options.filter(option => option.value === statusName && !option.disabled);
      if (exact.length !== 1 || !Number.isSafeInteger(Number(exact[0].key)) || Number(exact[0].key) <= 0) {
        throw new Error('The Status check type is missing or ambiguous.');
      }
      const type = {key: Number(exact[0].key), value: statusName};
      const where = [{field: 'type', value: type.key}, {field: 'is_active', value: 1}];
      return this.entities.getRecordAttributes('transporting_units', truckId, ['internal_number'], [
        {alias: 'arrival_checks', child_entity: 'check_points', foreign_key: 'transporting_unit', where},
        {alias: 'arrival_without_date', child_entity: 'check_points', foreign_key: 'transporting_unit',
          where: [...where, {field: 'status_end_date', value: null}]},
      ]).pipe(switchMap(lookup => {
        const all = this.ids(lookup.data['arrival_checks']);
        const missing = this.ids(lookup.data['arrival_without_date']);
        if (missing.some(id => !all.includes(id))) throw new Error('Status checks changed. Please reopen the reminder.');
        const id = missing[0] ?? all[0];
        if (id) {
          const edit = this.auth.can('check_points', 'edit');
          return forkJoin({
            attributes: this.entities.getRecordAttributes('check_points', id,
              ['transporting_unit', 'type', 'is_active', 'deleted_at']),
            form: edit ? this.entities.getEdit('check_points', id, 'inline') : this.entities.getShowEntity('check_points', id),
          }).pipe(map(({attributes, form}) => {
            const data = attributes.data;
            if (Number(data['transporting_unit']) !== truckId || Number(data['type']) !== type.key
              || Number(data['is_active']) !== 1 || data['deleted_at'] != null || Number(form.data.id) !== id) {
              throw new Error('Status checks changed. Please reopen the reminder.');
            }
            for (const field of form.data.fields) {
              const expected = field.name === 'type' ? type.key : field.name === 'transporting_unit' ? truckId : null;
              if (expected !== null && Number(field.props?.value?.key ?? field.props?.value) !== expected) {
                throw new Error('Status checks changed. Please reopen the reminder.');
              }
            }
            return {truckId, mode: edit && form.data._can_edit !== false ? 'edit' : 'view', form: form.data} as PreparedArrivalStatus;
          }));
        }
        if (!this.auth.can('check_points', 'create')) throw new Error('You do not have permission to add this Status check.');
        return this.entities.getCreate('check_points', 'inline', {id: truckId, name: 'transporting_units'}).pipe(map(response => {
          const form = response.data;
          const typeField = form.fields.find(field => field.name === 'type');
          if (Number(form.id) > 0 || !typeField || typeField.type !== 'dropdown'
            || ['hidden', 'readonly'].includes((typeField as any).status)) {
            throw new Error('Could not prepare the Status check form.');
          }
          return {truckId, mode: 'create', form: {...form, id: null, fields: form.fields.map(field => {
            const value = field.name === 'type' ? type : field.name === 'transporting_unit'
              ? {key: truckId, value: String(lookup.data['internal_number'] ?? truckId)}
              : field.name === 'status_end_date' ? null : field.props?.value;
            return {...field, props: {...field.props, value}};
          })}} as PreparedArrivalStatus;
        }));
      }));
    }));
  }

  private ids(value: unknown): number[] {
    if (!Array.isArray(value) || value.length > 500 || value.some(id => !Number.isSafeInteger(Number(id)) || Number(id) <= 0)) {
      throw new Error('Could not check existing Status checks. No new record was prepared.');
    }
    return [...new Set(value.map(Number))].sort((a, b) => a - b);
  }
}
